import SectionCallToAction from 'components/sections/sectionAppBanner/SectionCallToAction';
import SectionFilterMap from 'components/sections/sectionFilterMap/SectionFilterMap';
import SectionGlobalSearch from 'components/sections/sectionGlobalSearch/SectionGlobalSearch';
import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import { KioskType } from '../../../@types';
import { AppContext } from '../../../context/app';
import { useCancelSearchFocus } from '../../../hooks/useCancelSearchFocus';
import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useOutsideClickMultiple } from '../../../hooks/useOutsideClickMultiple';
import { CampusSelectorLandingPage } from '../../campusSelectorLandingPage/CampusSelectorLandingPage';
import { KioskKeyboard } from '../../kiosk-keyboard/kioskKeyboard';
import { PlaceSearchStateEnum } from '../../sections/sectionGlobalSearch/types';
import { CampusSelectorDropdown } from '../../shared/campusSelectorDropdown/CampusSelectorDropdown';
import Loading from '../../shared/loading/Loading';
import SearchInput from '../../shared/searchInput/SearchInput';
import TextButton from '../../shared/textButton/TextButton';
import Title from '../../shared/title/Title';
import { Tutorial } from '../../tutorial/TutorialModal';
import styles from './campusDirectory.module.scss';

const CampusDirectoryTemplate: React.FC = () => {
  const {
    selectedSite,
    showKeyboard,
    setShowKeyboard,
    setGlobalSearchText,
    globalSearchText,
    placeSearchState,
    setPlaceSearchState,
    setLegToggle,
    setNavStartingPlace,
    setNavDestinationPlace,
    setDirections,
  } = useContext(AppContext);

  useEffect(() => {
    setLegToggle(false);
    setNavStartingPlace(undefined);
    setNavDestinationPlace(undefined);
    setDirections(undefined);
  }, [
    setDirections,
    setLegToggle,
    setNavStartingPlace,
    setNavDestinationPlace,
  ]);

  const { kiosk, kioskMode } = useKioskManager();
  const keyboardRef = useRef(null);
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchFocus = useCallback(() => {
    !globalSearchText &&
      setPlaceSearchState(PlaceSearchStateEnum.SEARCHLANDING);
    setShowKeyboard(true);
  }, [globalSearchText, setPlaceSearchState, setShowKeyboard]);
  const handleClearSearchText = useCallback(() => {
    setGlobalSearchText(undefined);
    setShowKeyboard(false);
  }, [setGlobalSearchText, setShowKeyboard]);
  const handleCancelSearchFocus = useCancelSearchFocus();
  const searchOutsideClickRef = useRef<HTMLDivElement>(null);
  const keyboardOutsideClickRef = useRef<HTMLDivElement>(null);

  useOutsideClickMultiple(searchOutsideClickRef, keyboardOutsideClickRef, () =>
    setShowKeyboard(false),
  );

  // Campus directory for web and mobile
  const renderCampusDirectory = () => (
    <div className={`${styles.container}`}>
      <div className={`${styles.leftContainer}`}>
        <CampusSelectorDropdown />
        <section className={styles.sectionFilterMap}>
          <SectionFilterMap />
        </section>
      </div>
      <section className={styles.sectionSearchCategory}>
        <SectionGlobalSearch outsideClickRef={searchOutsideClickRef} />
        <SectionCallToAction
          hideCallToAction={!kioskMode || !!globalSearchText || showKeyboard}
          origin="CampusDirectory"
        />
      </section>
    </div>
  );

  const renderTutorial = useMemo(() => {
    if (!kioskMode && !isMobile) {
      return <Tutorial />;
    }
  }, [kioskMode, isMobile]);

  const renderKeyboard = useMemo(() => {
    if (showKeyboard && kiosk?.model !== KioskType.CTS_DIGITAL_SIGNAGE) {
      return (
        <div ref={keyboardOutsideClickRef}>
          <div className={styles.mapSearchBar}>
            <div className={styles.searchWrapper}>
              <SearchInput
                value={globalSearchText || ''}
                onChange={(value) => setGlobalSearchText(value)}
                placeholder={t('placeholders.search')}
                onFocus={handleSearchFocus}
                onCancel={handleClearSearchText}
                searchInputRef={searchInputRef}
              />
            </div>
            <TextButton
              onClick={handleCancelSearchFocus}
              label={t('labels.cancel')}
              side={'none'}
            />
          </div>
          <KioskKeyboard keyboardRef={keyboardRef}></KioskKeyboard>
        </div>
      );
    }
  }, [
    showKeyboard,
    kiosk,
    globalSearchText,
    handleCancelSearchFocus,
    handleSearchFocus,
    handleClearSearchText,
    setGlobalSearchText,
    t,
  ]);

  const renderCallToAction = useMemo(
    () => (
      <SectionCallToAction
        hideCallToAction={
          placeSearchState !== PlaceSearchStateEnum.DISPLAYCATEGORIES ||
          showKeyboard
        }
        origin="CampusDirectory"
      />
    ),
    [placeSearchState, showKeyboard],
  );

  return (
    <>
      {renderTutorial}
      {/*TODO rethink this solution to avoid using Suspense and loading component*/}
      {kioskMode ? (
        <div className={`${kiosk && kioskMode && styles[kiosk.model]}`}>
          <div className={styles.titleAndMapContainer}>
            {kiosk?.model !== KioskType.ELO_TOUCH && (
              <Title>{`${selectedSite?.short_name || selectedSite?.name}`}</Title>
            )}
            <section className={styles.mapKeyboardContainer}>
              <SectionFilterMap />
              {renderKeyboard}
            </section>
          </div>
          <section
            className={styles.sectionSearchCategory}
            ref={searchOutsideClickRef}
          >
            <SectionGlobalSearch outsideClickRef={searchOutsideClickRef} />
            {kiosk?.model !== KioskType.ELO_TOUCH && renderCallToAction}
          </section>
          {showKeyboard && kiosk?.model === KioskType.CTS_DIGITAL_SIGNAGE && (
            <div ref={keyboardOutsideClickRef}>
              <KioskKeyboard keyboardRef={keyboardRef}></KioskKeyboard>
            </div>
          )}
        </div>
      ) : (
        <Suspense fallback={<Loading isLoading={true} />}>
          {selectedSite ? (
            <>{renderCampusDirectory()}</>
          ) : (
            <CampusSelectorLandingPage />
          )}
        </Suspense>
      )}
    </>
  );
};

export default CampusDirectoryTemplate;
